import type { ArtworkDocument, ToolId } from '../core/types';
import { deserializeOperation, serializeOperation } from '../core/operations';
import { SCHEMA_VERSION } from '../core/version';
import { DEFAULT_COLOR, DEFAULT_TOOL } from '../core/config';

export { SCHEMA_VERSION };

/**
 * Save-schema definition, validation and migration.
 *
 * The persisted record is a plain JSON object. On load we NEVER trust it:
 * `parseDocument` validates every field and returns null for anything corrupt,
 * incompatible or foreign. Callers fall back to an empty document so invalid
 * saved data can never crash the game. Migration strategy is documented in
 * SAVE_SCHEMA.md; add a branch per old version here.
 */

export function createEmptyDocument(now: number = Date.now()): ArtworkDocument {
  return {
    version: SCHEMA_VERSION,
    operations: [],
    nextSeq: 0,
    selectedColor: DEFAULT_COLOR,
    selectedTool: DEFAULT_TOOL,
    updatedAt: now,
  };
}

/** Serialize a document to a JSON-safe plain object for storage. */
export function serializeDocument(doc: ArtworkDocument): Record<string, unknown> {
  return {
    version: doc.version,
    operations: doc.operations.map(serializeOperation),
    nextSeq: doc.nextSeq,
    selectedColor: doc.selectedColor,
    selectedTool: doc.selectedTool,
    updatedAt: doc.updatedAt,
  };
}

function isTool(v: unknown): v is ToolId {
  return v === 'marker' || v === 'eraser';
}

/**
 * Migrate a raw stored object of an older/unknown version to the current shape,
 * or return null if it cannot be safely migrated. P0A only knows version 1.
 */
function migrate(raw: Record<string, unknown>): Record<string, unknown> | null {
  const version = raw.version;
  if (version === SCHEMA_VERSION) return raw;
  // No older versions exist yet. Unknown/newer versions are not trusted.
  // Example future migration:
  //   if (version === 0) return { ...raw, version: 1, nextSeq: derive(...) };
  return null;
}

/**
 * Parse + validate an unknown stored value into an ArtworkDocument.
 * Returns null if the data is missing, corrupt, or an incompatible version.
 */
export function parseDocument(raw: unknown): ArtworkDocument | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const migrated = migrate(raw as Record<string, unknown>);
  if (!migrated) return null;

  const o = migrated;
  if (o.version !== SCHEMA_VERSION) return null;
  if (!Array.isArray(o.operations)) return null;

  const operations = [];
  for (const item of o.operations) {
    const op = deserializeOperation(item);
    if (!op) return null; // any bad operation invalidates the whole record
    operations.push(op);
  }
  // Ensure deterministic order regardless of stored order.
  operations.sort((a, b) => a.seq - b.seq);

  const maxSeq = operations.reduce((m, op) => Math.max(m, op.seq), -1);
  const nextSeq =
    typeof o.nextSeq === 'number' && Number.isFinite(o.nextSeq)
      ? Math.max(o.nextSeq, maxSeq + 1)
      : maxSeq + 1;

  const selectedColor = typeof o.selectedColor === 'string' ? o.selectedColor : DEFAULT_COLOR;
  const selectedTool = isTool(o.selectedTool) ? o.selectedTool : DEFAULT_TOOL;
  const updatedAt =
    typeof o.updatedAt === 'number' && Number.isFinite(o.updatedAt) ? o.updatedAt : Date.now();

  return {
    version: SCHEMA_VERSION,
    operations,
    nextSeq,
    selectedColor,
    selectedTool,
    updatedAt,
  };
}
